// src/cockpit4/components/BackupPanel.tsx
// ============================================
// 数据备份与恢复面板（设置中心 P0 - 功能3）
//
// 功能（来自改进计划文档 10.2 功能3）：
//   1. 自动定时备份（日/周/月）
//   2. 一键备份和恢复
//   3. 备份验证（校验完整性）
//   4. 灾难恢复预案（步骤指引）
//   5. 备份存储管理（查看占用 / 清理旧备份）
//   6. 备份加密（轻量混淆）
//
// 数据来源：
//   - 备份记录持久化在 localStorage（key: xeaos_backups），无任何假数据
//   - 实际备份内容 = 所有 xeaos_ 前缀 localStorage key 的 JSON 快照
//   - 备份配置持久化在 localStorage（key: xeaos_backup_config）
//   - 无数据时显示"暂无备份 / 点击创建"
//
// 权限：仅超级管理员（super_admin）可操作，普通员工只读
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database,
  Archive,
  RotateCcw,
  Trash2,
  ShieldCheck,
  Lock,
  Unlock,
  RefreshCw,
  Loader2,
  Plus,
  Clock,
  Calendar,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Settings,
  ChevronDown,
  ChevronRight,
  Eye,
  Zap,
  FileText,
  Activity,
} from "lucide-react";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义（对齐改进计划文档数据模型）
// ============================================

type BackupType = "manual" | "auto_daily" | "auto_weekly" | "auto_monthly";
type BackupStatus = "creating" | "completed" | "failed" | "restoring" | "restored";
type BackupScope = "full" | "incremental";

interface BackupRecord {
  id: string;
  type: BackupType;
  scope: BackupScope;
  status: BackupStatus;
  /** 备份包含的模块（localStorage key 列表） */
  modules: string[];
  /** 备份大小（字节） */
  sizeBytes: number;
  /** 数据快照：{ localStorageKey: value } */
  snapshot: Record<string, string>;
  /** 校验和（用于完整性验证） */
  checksum?: string;
  /** 是否已验证完整性 */
  verified: boolean;
  /** 创建时间 ISO */
  createdAt: string;
  /** 操作人 */
  createdBy: string;
  /** 恢复时间 */
  restoredAt?: string;
  /** 恢复操作人 */
  restoredBy?: string;
  /** 备注（含加密标记等） */
  note?: string;
}

interface BackupConfig {
  /** 自动备份开关 */
  autoBackupEnabled: boolean;
  /** 频率 */
  frequency: "daily" | "weekly" | "monthly";
  /** 执行时间 HH:mm */
  time: string;
  /** 最大保留数量 */
  maxRetention: number;
  /** 加密开关 */
  encryptionEnabled: boolean;
  /** 包含的模块（空数组 = 全部 xeaos_ key） */
  modules: string[];
}

// ============================================
// 常量
// ============================================

const BACKUPS_KEY = "xeaos_backups";
const CONFIG_KEY = "xeaos_backup_config";
const LAST_AUTO_KEY = "xeaos_backup_last_auto";

/** 加密标记（写入 note 字段，标识该备份已加密） */
const ENC_MARKER = "🔒ENC";

const DEFAULT_CONFIG: BackupConfig = {
  autoBackupEnabled: false,
  frequency: "daily",
  time: "03:00",
  maxRetention: 10,
  encryptionEnabled: false,
  modules: [],
};

/** 已知模块的中文标签（其他 xeaos_ key 显示原始 key） */
const MODULE_LABELS: Record<string, string> = {
  xeaos_session_v2: "用户会话",
  xeaos_super_admin_v2: "超管配置",
  xeaos_employees_v2: "员工数据",
  xeaos_backup_config: "备份配置",
};

/** 备份类型配置 */
const TYPE_CONFIG: Record<BackupType, { label: string; color: string }> = {
  manual: { label: "手动", color: "#60A5FA" },
  auto_daily: { label: "自动·日", color: "#34D399" },
  auto_weekly: { label: "自动·周", color: "#A78BFA" },
  auto_monthly: { label: "自动·月", color: "#FBBF24" },
};

/** 备份状态配置 */
const STATUS_CONFIG: Record<BackupStatus, { label: string; color: string }> = {
  creating: { label: "创建中", color: "#FBBF24" },
  completed: { label: "已完成", color: "#34D399" },
  failed: { label: "失败", color: "#F87171" },
  restoring: { label: "恢复中", color: "#60A5FA" },
  restored: { label: "已恢复", color: "#A78BFA" },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `bk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 读取所有备份记录（按时间倒序） */
function loadBackups(): BackupRecord[] {
  try {
    const raw = localStorage.getItem(BACKUPS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BackupRecord[];
    if (!Array.isArray(parsed)) return [];
    return parsed.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  } catch {
    return [];
  }
}

/** 保存备份记录 */
function saveBackups(list: BackupRecord[]): void {
  try {
    localStorage.setItem(BACKUPS_KEY, JSON.stringify(list));
  } catch (err) {
    console.warn("[BackupPanel] 保存备份记录失败:", err);
  }
}

/** 读取备份配置 */
function loadConfig(): BackupConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return { ...DEFAULT_CONFIG };
    const parsed = JSON.parse(raw) as Partial<BackupConfig>;
    return { ...DEFAULT_CONFIG, ...parsed };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

/** 保存备份配置 */
function saveConfig(cfg: BackupConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
  } catch (err) {
    console.warn("[BackupPanel] 保存备份配置失败:", err);
  }
}

/** 发现所有 xeaos_ 前缀的 localStorage key（排除备份记录自身，避免递归） */
function discoverXeaosKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith("xeaos_") && k !== BACKUPS_KEY) {
      keys.push(k);
    }
  }
  return keys.sort();
}

/** 根据配置的 modules 过滤需要备份的 key（modules 为空表示全部） */
function resolveTargetKeys(cfg: BackupConfig): string[] {
  const all = discoverXeaosKeys();
  if (!cfg.modules.length) return all;
  return all.filter((k) => cfg.modules.includes(k));
}

/** 创建快照：读取指定 key 的当前值 */
function createSnapshot(keys: string[]): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const k of keys) {
    snapshot[k] = localStorage.getItem(k) ?? "";
  }
  return snapshot;
}

/**
 * 计算校验和（基于 FNV-1a 哈希，用于完整性验证，检测数据损坏/篡改）
 * 对 key 排序以保证确定性。返回形如 "fnv:xxxxxxxxxxxxxxxx" 的字符串。
 */
function computeChecksum(snapshot: Record<string, string>): string {
  const keys = Object.keys(snapshot).sort();
  let h1 = 0x811c9dc5;
  let h2 = 0x1000193;
  for (const k of keys) {
    const s = k + "\u0000" + (snapshot[k] ?? "");
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
      h2 = Math.imul(h2 ^ (c + 0x9e3779b9), 0x85ebca6b) >>> 0;
    }
  }
  return `fnv:${h1.toString(16).padStart(8, "0")}${h2.toString(16).padStart(8, "0")}`;
}

/** 轻量级加密：Base64 编码（非密码学加密，防止明文直读） */
function encryptValue(v: string): string {
  try {
    return btoa(unescape(encodeURIComponent(v)));
  } catch {
    return v;
  }
}

/** 轻量级解密：Base64 解码 */
function decryptValue(v: string): string {
  try {
    return decodeURIComponent(escape(atob(v)));
  } catch {
    return v;
  }
}

/** 字节大小格式化 */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/** 时间格式化 */
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** 相对时间（如 "3 分钟前"） */
function formatRelative(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "刚刚";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} 小时前`;
    return `${Math.floor(diff / 86_400_000)} 天前`;
  } catch {
    return iso;
  }
}

/** 获取模块中文标签 */
function moduleLabel(k: string): string {
  return MODULE_LABELS[k] ?? k;
}

/**
 * 判断是否应该执行自动备份
 * 仅在客户端面板挂载期间检查（应用关闭时不执行）
 */
function shouldAutoBackup(cfg: BackupConfig, lastAutoIso: string | null): boolean {
  if (!cfg.autoBackupEnabled) return false;
  const now = new Date();
  const [hh, mm] = cfg.time.split(":").map((x) => parseInt(x, 10) || 0);
  // 当前时间是否已到配置的执行时刻
  const reachedTime = now.getHours() > hh || (now.getHours() === hh && now.getMinutes() >= mm);

  if (!lastAutoIso) return reachedTime;

  const last = new Date(lastAutoIso);
  const sameDay = last.toDateString() === now.toDateString();

  if (cfg.frequency === "daily") {
    // 上次不是今天，且今天已到执行时刻
    return !sameDay && reachedTime;
  }
  if (cfg.frequency === "weekly") {
    // 距上次 >= 7 天，且已到执行时刻
    const diffDays = (now.getTime() - last.getTime()) / 86_400_000;
    return diffDays >= 7 && reachedTime;
  }
  // monthly
  const diffDays = (now.getTime() - last.getTime()) / 86_400_000;
  return diffDays >= 28 && reachedTime;
}

// ============================================
// 主组件
// ============================================

export default function BackupPanel() {
  const currentUser = useUserStore((s) => s.currentUser);
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [backups, setBackups] = useState<BackupRecord[]>([]);
  const [config, setConfig] = useState<BackupConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);

  // 创建备份表单
  const [createScope, setCreateScope] = useState<BackupScope>("full");
  const [createNote, setCreateNote] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  /** 刷新所有状态 */
  const refresh = useCallback(() => {
    setBackups(loadBackups());
    setConfig(loadConfig());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** 自动备份调度：每 60 秒检查一次 */
  useEffect(() => {
    if (!isSuperAdmin) return;
    const timer = window.setInterval(() => {
      const cfg = loadConfig();
      const lastAuto = localStorage.getItem(LAST_AUTO_KEY);
      if (shouldAutoBackup(cfg, lastAuto)) {
        // 触发自动备份
        void autoCreate(cfg);
      }
    }, 60_000);
    // 挂载时立即检查一次
    const cfg = loadConfig();
    const lastAuto = localStorage.getItem(LAST_AUTO_KEY);
    if (shouldAutoBackup(cfg, lastAuto)) {
      void autoCreate(cfg);
    }
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  /** 自动创建备份（内部使用，不依赖组件 state） */
  const autoCreate = useCallback(
    async (cfg: BackupConfig) => {
      const user = useUserStore.getState().currentUser;
      const keys = resolveTargetKeys(cfg);
      if (keys.length === 0) return;
      const rawSnapshot = createSnapshot(keys);
      const snapshot = cfg.encryptionEnabled
        ? Object.fromEntries(Object.entries(rawSnapshot).map(([k, v]) => [k, encryptValue(v)]))
        : rawSnapshot;
      const sizeBytes = new TextEncoder().encode(JSON.stringify(snapshot)).length;
      const type: BackupType =
        cfg.frequency === "daily" ? "auto_daily" : cfg.frequency === "weekly" ? "auto_weekly" : "auto_monthly";
      const note = cfg.encryptionEnabled ? ENC_MARKER : undefined;
      const record: BackupRecord = {
        id: genId(),
        type,
        scope: "full",
        status: "completed",
        modules: keys,
        sizeBytes,
        snapshot,
        checksum: computeChecksum(snapshot),
        verified: false,
        createdAt: new Date().toISOString(),
        createdBy: user?.username ?? "auto",
        note,
      };
      const list = loadBackups();
      list.unshift(record);
      // 应用保留策略
      const trimmed = list.slice(0, Math.max(1, cfg.maxRetention));
      saveBackups(trimmed);
      localStorage.setItem(LAST_AUTO_KEY, record.createdAt);
      setBackups(trimmed);
      console.log("[BackupPanel] 自动备份完成:", record.id, type);
    },
    []
  );

  /** 手动创建备份 */
  const handleCreate = useCallback(async () => {
    if (!isSuperAdmin) return;
    setBusy(true);
    setMessage(null);
    try {
      const cfg = loadConfig();
      const keys = resolveTargetKeys(cfg);
      if (keys.length === 0) {
        setMessage({ type: "error", text: "未发现任何 xeaos_ 数据，无法创建备份" });
        return;
      }

      // 增量备份：只包含与最近一次备份相比有变更的 key
      let keysToSnapshot = keys;
      if (createScope === "incremental") {
        const last = loadBackups()[0];
        if (last) {
          keysToSnapshot = keys.filter((k) => {
            const cur = localStorage.getItem(k) ?? "";
            const prevEncrypted = last.note?.includes(ENC_MARKER);
            const prev = prevEncrypted ? decryptValue(last.snapshot[k] ?? "") : last.snapshot[k] ?? "";
            return cur !== prev;
          });
        }
      }

      const rawSnapshot = createSnapshot(keysToSnapshot);
      const snapshot = cfg.encryptionEnabled
        ? Object.fromEntries(Object.entries(rawSnapshot).map(([k, v]) => [k, encryptValue(v)]))
        : rawSnapshot;
      const sizeBytes = new TextEncoder().encode(JSON.stringify(snapshot)).length;

      // 写入一条 creating 记录，再更新为 completed（真实反映创建过程）
      const creatingId = genId();
      const creatingRecord: BackupRecord = {
        id: creatingId,
        type: "manual",
        scope: createScope,
        status: "creating",
        modules: keysToSnapshot,
        sizeBytes,
        snapshot,
        checksum: undefined,
        verified: false,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.username ?? "unknown",
        note: buildNote(createNote, cfg.encryptionEnabled),
      };
      const list0 = loadBackups();
      list0.unshift(creatingRecord);
      saveBackups(list0);
      setBackups([...list0]);

      // 模拟快照写入耗时（让 creating 状态可见）
      await new Promise((r) => setTimeout(r, 350));

      const checksum = computeChecksum(snapshot);
      const finalList = loadBackups().map((r) =>
        r.id === creatingId
          ? { ...r, status: "completed" as BackupStatus, checksum }
          : r
      );
      // 应用保留策略
      const trimmed = finalList.slice(0, Math.max(1, cfg.maxRetention));
      saveBackups(trimmed);
      setBackups(trimmed);

      if (createScope === "incremental" && keysToSnapshot.length === 0) {
        setMessage({ type: "info", text: "增量备份完成：自上次备份以来无数据变更" });
      } else {
        setMessage({ type: "success", text: `备份成功（${keysToSnapshot.length} 个模块，${formatBytes(sizeBytes)}）` });
      }
      setCreateNote("");
      setShowCreateForm(false);
    } catch (err) {
      console.error("[BackupPanel] 创建备份失败:", err);
      setMessage({ type: "error", text: "创建备份失败" });
    } finally {
      setBusy(false);
    }
  }, [isSuperAdmin, createScope, createNote, currentUser]);

  /** 恢复备份 */
  const handleRestore = useCallback(
    async (record: BackupRecord) => {
      if (!isSuperAdmin) return;
      const confirmed = window.confirm(
        `确认恢复此备份？\n\n` +
          `创建时间：${formatTime(record.createdAt)}\n` +
          `包含模块：${record.modules.length} 个\n` +
          `操作人：${record.createdBy}\n\n` +
          `⚠️ 恢复将覆盖当前 localStorage 中对应的 xeaos_ 数据，此操作不可撤销。`
      );
      if (!confirmed) return;

      setBusy(true);
      setMessage(null);
      try {
        // 标记 restoring
        const list0 = loadBackups().map((r) =>
          r.id === record.id ? { ...r, status: "restoring" as BackupStatus } : r
        );
        saveBackups(list0);
        setBackups([...list0]);

        await new Promise((r) => setTimeout(r, 300));

        const isEnc = record.note?.includes(ENC_MARKER);
        // 写回 localStorage
        for (const k of Object.keys(record.snapshot)) {
          const val = isEnc ? decryptValue(record.snapshot[k]) : record.snapshot[k];
          localStorage.setItem(k, val);
        }

        // 更新状态为 restored
        const finalList = loadBackups().map((r) =>
          r.id === record.id
            ? {
                ...r,
                status: "restored" as BackupStatus,
                restoredAt: new Date().toISOString(),
                restoredBy: currentUser?.username ?? "unknown",
              }
            : r
        );
        saveBackups(finalList);
        setBackups([...finalList]);
        setMessage({
          type: "success",
          text: `恢复成功（已写回 ${Object.keys(record.snapshot).length} 个 key），建议刷新页面使所有模块生效`,
        });
      } catch (err) {
        console.error("[BackupPanel] 恢复失败:", err);
        const failList = loadBackups().map((r) =>
          r.id === record.id ? { ...r, status: "failed" as BackupStatus } : r
        );
        saveBackups(failList);
        setBackups([...failList]);
        setMessage({ type: "error", text: "恢复失败" });
      } finally {
        setBusy(false);
      }
    },
    [isSuperAdmin, currentUser]
  );

  /** 验证备份完整性 */
  const handleVerify = useCallback((record: BackupRecord) => {
    if (!isSuperAdmin) return;
    const current = computeChecksum(record.snapshot);
    const ok = record.checksum ? current === record.checksum : false;
    const list = loadBackups().map((r) => (r.id === record.id ? { ...r, verified: ok } : r));
    saveBackups(list);
    setBackups([...list]);
    setMessage(
      ok
        ? { type: "success", text: "完整性验证通过：校验和匹配" }
        : { type: "error", text: "完整性验证失败：校验和不匹配，数据可能已损坏" }
    );
  }, [isSuperAdmin]);

  /** 删除单个备份 */
  const handleDelete = useCallback((id: string) => {
    if (!isSuperAdmin) return;
    if (!window.confirm("确认删除此备份记录？此操作不可撤销。")) return;
    const list = loadBackups().filter((r) => r.id !== id);
    saveBackups(list);
    setBackups([...list]);
    setMessage({ type: "info", text: "已删除备份记录" });
  }, [isSuperAdmin]);

  /** 清理超出保留数量的旧备份 */
  const handleCleanOld = useCallback(() => {
    if (!isSuperAdmin) return;
    const cfg = loadConfig();
    const list = loadBackups();
    const keep = Math.max(1, cfg.maxRetention);
    if (list.length <= keep) {
      setMessage({ type: "info", text: `当前 ${list.length} 条，未超过保留上限 ${keep}，无需清理` });
      return;
    }
    if (!window.confirm(`将删除 ${list.length - keep} 条旧备份，保留最近 ${keep} 条，确认？`)) return;
    const trimmed = list.slice(0, keep);
    saveBackups(trimmed);
    setBackups([...trimmed]);
    setMessage({ type: "success", text: `已清理 ${list.length - keep} 条旧备份` });
  }, [isSuperAdmin]);

  /** 清空全部备份 */
  const handleClearAll = useCallback(() => {
    if (!isSuperAdmin) return;
    if (!window.confirm("⚠️ 确认清空所有备份记录？此操作不可撤销，请确保已导出重要备份。")) return;
    saveBackups([]);
    setBackups([]);
    setMessage({ type: "info", text: "已清空所有备份记录" });
  }, [isSuperAdmin]);

  /** 更新配置 */
  const updateConfig = useCallback((patch: Partial<BackupConfig>) => {
    if (!isSuperAdmin) return;
    const next = { ...loadConfig(), ...patch };
    saveConfig(next);
    setConfig(next);
  }, [isSuperAdmin]);

  // 统计数据
  const stats = useMemo(() => {
    const total = backups.length;
    const totalBytes = backups.reduce((s, r) => s + r.sizeBytes, 0);
    const lastTime = backups[0]?.createdAt ?? null;
    const verifiedCount = backups.filter((r) => r.verified).length;
    return { total, totalBytes, lastTime, verifiedCount };
  }, [backups]);

  // 已发现的模块列表（用于配置面板勾选）
  const discoveredModules = useMemo(() => discoverXeaosKeys(), [backups, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[12px] text-gray-500">
        <Loader2 size={14} className="animate-spin mr-2" /> 加载备份面板...
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-4"
    >
      {/* 顶部统计卡片 + 立即备份 */}
      <StatsBar
        stats={stats}
        config={config}
        isSuperAdmin={isSuperAdmin}
        busy={busy}
        onRefresh={refresh}
        onCreate={() => setShowCreateForm((v) => !v)}
      />

      {/* 创建备份表单 */}
      <AnimatePresence>
        {showCreateForm && isSuperAdmin && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <CreateBackupForm
              scope={createScope}
              setScope={setCreateScope}
              note={createNote}
              setNote={setCreateNote}
              busy={busy}
              onSubmit={handleCreate}
              onCancel={() => setShowCreateForm(false)}
              moduleCount={discoveredModules.length}
              selectedModuleCount={config.modules.length === 0 ? discoveredModules.length : config.modules.length}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 消息提示 */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border ${
              message.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/20"
                : message.type === "error"
                ? "bg-red-500/10 border-red-500/20"
                : "bg-sky-500/10 border-sky-500/20"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0" />
            ) : message.type === "error" ? (
              <XCircle size={13} className="text-red-400 flex-shrink-0" />
            ) : (
              <Activity size={13} className="text-sky-400 flex-shrink-0" />
            )}
            <span
              className={`text-[11px] ${
                message.type === "success"
                  ? "text-emerald-300"
                  : message.type === "error"
                  ? "text-red-300"
                  : "text-sky-300"
              }`}
            >
              {message.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 中部：左备份列表 + 右配置面板 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 左：备份列表 */}
        <div className="lg:col-span-2">
          <BackupList
            backups={backups}
            isSuperAdmin={isSuperAdmin}
            busy={busy}
            onRestore={handleRestore}
            onVerify={handleVerify}
            onDelete={handleDelete}
            onCleanOld={handleCleanOld}
            onClearAll={handleClearAll}
          />
        </div>

        {/* 右：配置面板 */}
        <div className="lg:col-span-1">
          <ConfigPanel
            config={config}
            isSuperAdmin={isSuperAdmin}
            discoveredModules={discoveredModules}
            onUpdate={updateConfig}
          />
        </div>
      </div>

      {/* 底部：灾难恢复预案 */}
      <DisasterRecoveryPlan />
    </motion.div>
  );
}

// ============================================
// 构建备注（合并用户备注与加密标记）
// ============================================

function buildNote(userNote: string, encrypted: boolean): string | undefined {
  const parts: string[] = [];
  if (encrypted) parts.push(ENC_MARKER);
  if (userNote.trim()) parts.push(userNote.trim());
  return parts.length ? parts.join(" ") : undefined;
}

// ============================================
// 统计栏组件
// ============================================

interface StatsBarProps {
  stats: { total: number; totalBytes: number; lastTime: string | null; verifiedCount: number };
  config: BackupConfig;
  isSuperAdmin: boolean;
  busy: boolean;
  onRefresh: () => void;
  onCreate: () => void;
}

function StatsBar({ stats, config, isSuperAdmin, busy, onRefresh, onCreate }: StatsBarProps) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
            <Database size={14} className="text-sky-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">数据备份与恢复</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              localStorage 快照 · 仅超管可操作
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            title="刷新"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
          {isSuperAdmin && (
            <button
              onClick={onCreate}
              disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500/15 border border-sky-500/30 text-sky-300 text-[11px] font-medium hover:bg-sky-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              立即备份
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard
          icon={<Archive size={12} className="text-sky-400" />}
          label="备份总数"
          value={String(stats.total)}
          sub={stats.verifiedCount > 0 ? `${stats.verifiedCount} 已验证` : "未验证"}
          color="#60A5FA"
        />
        <StatCard
          icon={<Clock size={12} className="text-emerald-400" />}
          label="最近备份"
          value={stats.lastTime ? formatRelative(stats.lastTime) : "—"}
          sub={stats.lastTime ? formatTime(stats.lastTime) : "暂无备份"}
          color="#34D399"
        />
        <StatCard
          icon={<HardDrive size={12} className="text-amber-400" />}
          label="总占用"
          value={formatBytes(stats.totalBytes)}
          sub={`${stats.total} 条记录`}
          color="#FBBF24"
        />
        <StatCard
          icon={<Zap size={12} className={config.autoBackupEnabled ? "text-violet-400" : "text-gray-600"} />}
          label="自动备份"
          value={config.autoBackupEnabled ? "已启用" : "未启用"}
          sub={config.autoBackupEnabled ? `${freqLabel(config.frequency)} · ${config.time}` : "点击右侧配置开启"}
          color={config.autoBackupEnabled ? "#A78BFA" : "#6B7280"}
        />
      </div>
    </div>
  );
}

function freqLabel(f: BackupConfig["frequency"]): string {
  return f === "daily" ? "每日" : f === "weekly" ? "每周" : "每月";
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  color: string;
}

function StatCard({ icon, label, value, sub, color }: StatCardProps) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-[15px] font-semibold text-white truncate" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] font-mono text-gray-600 truncate mt-0.5">{sub}</div>
    </div>
  );
}

// ============================================
// 创建备份表单
// ============================================

interface CreateBackupFormProps {
  scope: BackupScope;
  setScope: (s: BackupScope) => void;
  note: string;
  setNote: (s: string) => void;
  busy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  moduleCount: number;
  selectedModuleCount: number;
}

function CreateBackupForm({
  scope,
  setScope,
  note,
  setNote,
  busy,
  onSubmit,
  onCancel,
  moduleCount,
  selectedModuleCount,
}: CreateBackupFormProps) {
  return (
    <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.03] backdrop-blur-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Plus size={13} className="text-sky-400" />
        <span className="text-[12px] font-medium text-white">创建新备份</span>
        <span className="text-[10px] font-mono text-gray-500">
          将备份 {selectedModuleCount}/{moduleCount} 个 xeaos_ 模块
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        {/* 备份范围 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">备份范围</label>
          <div className="flex gap-1 mt-1">
            <button
              onClick={() => setScope("full")}
              className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium border transition-all ${
                scope === "full"
                  ? "bg-sky-500/20 border-sky-500/40 text-sky-300"
                  : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.05]"
              }`}
            >
              完整备份
            </button>
            <button
              onClick={() => setScope("incremental")}
              className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium border transition-all ${
                scope === "incremental"
                  ? "bg-sky-500/20 border-sky-500/40 text-sky-300"
                  : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.05]"
              }`}
            >
              增量备份
            </button>
          </div>
        </div>

        {/* 备注 */}
        <div className="md:col-span-1">
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">备注（可选）</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例如：版本发布前备份"
            maxLength={80}
            className="w-full mt-1 px-2 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-sky-500/40"
          />
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-2">
          <button
            onClick={onSubmit}
            disabled={busy || moduleCount === 0}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500/20 border border-sky-500/40 text-sky-300 text-[11px] font-medium hover:bg-sky-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
            确认创建
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] text-gray-400 text-[11px] hover:bg-white/[0.05] transition-all"
          >
            取消
          </button>
        </div>
      </div>

      {scope === "incremental" && (
        <div className="mt-2 text-[10px] font-mono text-amber-400/80">
          ℹ️ 增量备份仅包含自最近一次备份以来有变更的模块；恢复时会在当前数据上叠加写入。
        </div>
      )}
    </div>
  );
}

// ============================================
// 备份列表组件
// ============================================

interface BackupListProps {
  backups: BackupRecord[];
  isSuperAdmin: boolean;
  busy: boolean;
  onRestore: (r: BackupRecord) => void;
  onVerify: (r: BackupRecord) => void;
  onDelete: (id: string) => void;
  onCleanOld: () => void;
  onClearAll: () => void;
}

function BackupList({
  backups,
  isSuperAdmin,
  busy,
  onRestore,
  onVerify,
  onDelete,
  onCleanOld,
  onClearAll,
}: BackupListProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Archive size={14} className="text-sky-400" />
          <span className="text-[13px] font-medium text-white">备份列表</span>
          <span className="text-[10px] font-mono text-gray-600">{backups.length} 条 · 按时间倒序</span>
        </div>
        {isSuperAdmin && backups.length > 0 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={onCleanOld}
              title="按保留策略清理旧备份"
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
            >
              <Trash2 size={10} className="text-amber-400" />
              <span className="text-[10px] font-mono text-amber-300">清理旧备份</span>
            </button>
            <button
              onClick={onClearAll}
              title="清空全部备份"
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-colors"
            >
              <XCircle size={10} className="text-red-400" />
              <span className="text-[10px] font-mono text-red-300">清空</span>
            </button>
          </div>
        )}
      </div>

      {/* 列表 */}
      <div className="max-h-[560px] overflow-y-auto">
        {backups.length === 0 ? (
          <div className="py-12 text-center">
            <Database size={28} className="mx-auto text-gray-700 mb-2" />
            <div className="text-[12px] text-gray-500">暂无备份 / 点击创建</div>
            <div className="text-[10px] font-mono text-gray-700 mt-1">
              点击顶部"立即备份"按钮，创建第一条数据快照
            </div>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.03]">
            {backups.map((r) => {
              const isExp = expanded.has(r.id);
              const typeCfg = TYPE_CONFIG[r.type];
              const statusCfg = STATUS_CONFIG[r.status];
              const isEnc = r.note?.includes(ENC_MARKER);
              return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-3 py-2.5 hover:bg-white/[0.01] transition-colors"
                  >
                    {/* 行主体 */}
                    <div className="flex items-center gap-2.5">
                      {/* 类型色块 */}
                      <div
                        className="w-1 h-9 rounded-full flex-shrink-0"
                        style={{ background: typeCfg.color }}
                      />
                      {/* 展开按钮 */}
                      <button
                        onClick={() => toggle(r.id)}
                        className="flex-shrink-0 text-gray-500 hover:text-gray-300"
                      >
                        {isExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>

                      {/* 主信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[12px] font-medium text-white">{typeCfg.label}</span>
                          <span
                            className="text-[9px] font-mono px-1.5 py-0.5 rounded border"
                            style={{ color: statusCfg.color, borderColor: statusCfg.color + "40", background: statusCfg.color + "15" }}
                          >
                            {statusCfg.label}
                          </span>
                          {r.scope === "incremental" && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300">
                              增量
                            </span>
                          )}
                          {isEnc && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-300 flex items-center gap-0.5">
                              <Lock size={9} /> 加密
                            </span>
                          )}
                          {r.verified && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 flex items-center gap-0.5">
                              <ShieldCheck size={9} /> 已验证
                            </span>
                          )}
                          <span className="text-[10px] font-mono text-gray-500">
                            {r.modules.length} 模块 · {formatBytes(r.sizeBytes)}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-gray-600 mt-0.5 truncate">
                          {formatTime(r.createdAt)} · {r.createdBy}
                          {r.restoredAt && ` · 恢复于 ${formatTime(r.restoredAt)} (${r.restoredBy})`}
                          {r.note && !r.note.includes(ENC_MARKER) && ` · ${r.note}`}
                        </div>
                      </div>

                      {/* 操作按钮 */}
                      {isSuperAdmin && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <IconBtn
                            title="验证完整性"
                            onClick={() => onVerify(r)}
                            disabled={busy || r.status === "creating"}
                            color="#34D399"
                          >
                            <ShieldCheck size={11} />
                          </IconBtn>
                          <IconBtn
                            title="恢复此备份"
                            onClick={() => onRestore(r)}
                            disabled={busy || r.status === "creating" || r.status === "restoring"}
                            color="#60A5FA"
                          >
                            <RotateCcw size={11} />
                          </IconBtn>
                          <IconBtn
                            title="删除"
                            onClick={() => onDelete(r.id)}
                            disabled={busy}
                            color="#F87171"
                          >
                            <Trash2 size={11} />
                          </IconBtn>
                        </div>
                      )}
                    </div>

                    {/* 展开详情 */}
                    <AnimatePresence>
                      {isExp && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-2 ml-7 rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5 space-y-1.5">
                            <DetailRow label="备份 ID" value={r.id} mono />
                            <DetailRow label="校验和" value={r.checksum ?? "未计算"} mono />
                            <DetailRow
                              label="完整性"
                              value={r.verified ? "✅ 已验证通过" : r.checksum ? "⚠️ 未验证" : "—"}
                            />
                            <DetailRow
                              label="恢复历史"
                              value={
                                r.restoredAt
                                  ? `${formatTime(r.restoredAt)} · ${r.restoredBy}`
                                  : "从未恢复"
                              }
                            />
                            <div>
                              <div className="text-[10px] font-mono text-gray-500 mb-1">包含模块（{r.modules.length}）</div>
                              <div className="flex flex-wrap gap-1">
                                {r.modules.length === 0 ? (
                                  <span className="text-[10px] font-mono text-gray-600 italic">无（增量无变更）</span>
                                ) : (
                                  r.modules.map((m) => (
                                    <span
                                      key={m}
                                      className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-gray-300 border border-white/[0.05]"
                                    >
                                      {moduleLabel(m)}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

/** 小图标按钮 */
function IconBtn({
  title,
  onClick,
  disabled,
  color,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="p-1.5 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      style={{ color }}
    >
      {children}
    </button>
  );
}

/** 详情行 */
function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span className="font-mono text-gray-500 w-16 flex-shrink-0">{label}</span>
      <span className={`text-gray-300 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

// ============================================
// 配置面板组件
// ============================================

interface ConfigPanelProps {
  config: BackupConfig;
  isSuperAdmin: boolean;
  discoveredModules: string[];
  onUpdate: (patch: Partial<BackupConfig>) => void;
}

function ConfigPanel({ config, isSuperAdmin, discoveredModules, onUpdate }: ConfigPanelProps) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      {/* 头部 */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
        <Settings size={14} className="text-violet-400" />
        <span className="text-[13px] font-medium text-white">备份配置</span>
      </div>

      <div className="p-3 space-y-3">
        {/* 自动备份开关 */}
        <ToggleRow
          icon={<Zap size={12} className="text-violet-400" />}
          label="自动定时备份"
          desc="启用后按频率自动创建快照"
          checked={config.autoBackupEnabled}
          disabled={!isSuperAdmin}
          onChange={(v) => onUpdate({ autoBackupEnabled: v })}
        />

        {/* 频率 */}
        <div>
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            <Calendar size={11} className="text-sky-400" /> 备份频率
          </label>
          <div className="flex gap-1">
            {(["daily", "weekly", "monthly"] as const).map((f) => (
              <button
                key={f}
                onClick={() => isSuperAdmin && onUpdate({ frequency: f })}
                disabled={!isSuperAdmin}
                className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  config.frequency === f
                    ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.05]"
                }`}
              >
                {freqLabel(f)}
              </button>
            ))}
          </div>
        </div>

        {/* 执行时间 */}
        <div>
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            <Clock size={11} className="text-emerald-400" /> 执行时间
          </label>
          <input
            type="time"
            value={config.time}
            onChange={(e) => isSuperAdmin && onUpdate({ time: e.target.value })}
            disabled={!isSuperAdmin}
            className="w-full px-2 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.06] text-[11px] text-gray-200 focus:outline-none focus:border-violet-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* 保留数量 */}
        <div>
          <label className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            <Archive size={11} className="text-amber-400" /> 最大保留数量
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={3}
              max={50}
              step={1}
              value={config.maxRetention}
              onChange={(e) => isSuperAdmin && onUpdate({ maxRetention: parseInt(e.target.value, 10) })}
              disabled={!isSuperAdmin}
              className="flex-1 accent-violet-500 disabled:opacity-50"
            />
            <span className="text-[11px] font-mono text-violet-300 w-12 text-right">{config.maxRetention} 条</span>
          </div>
          <div className="text-[9px] font-mono text-gray-600 mt-0.5">超出保留数量时自动清理最旧的备份</div>
        </div>

        {/* 加密开关 */}
        <ToggleRow
          icon={config.encryptionEnabled ? <Lock size={12} className="text-violet-400" /> : <Unlock size={12} className="text-gray-500" />}
          label="备份加密"
          desc="启用后使用 Base64 对快照编码保护（轻量混淆，防明文直读）"
          checked={config.encryptionEnabled}
          disabled={!isSuperAdmin}
          onChange={(v) => onUpdate({ encryptionEnabled: v })}
        />

        {/* 模块选择 */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="flex items-center gap-1.5 text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              <Database size={11} className="text-sky-400" /> 备份模块
            </label>
            {isSuperAdmin && (
              <button
                onClick={() =>
                  onUpdate({
                    modules: config.modules.length === 0 ? discoveredModules.slice() : [],
                  })
                }
                className="text-[10px] font-mono text-sky-400 hover:text-sky-300"
              >
                {config.modules.length === 0 ? "取消全选" : "全选"}
              </button>
            )}
          </div>
          <div className="text-[9px] font-mono text-gray-600 mb-1.5">
            {config.modules.length === 0
              ? `全部模块（${discoveredModules.length}）`
              : `已选 ${config.modules.length}/${discoveredModules.length}`}
          </div>
          <div className="max-h-[140px] overflow-y-auto space-y-1 rounded-md border border-white/[0.04] bg-white/[0.01] p-1.5">
            {discoveredModules.length === 0 ? (
              <div className="text-[10px] font-mono text-gray-600 italic py-2 text-center">
                未发现 xeaos_ 数据
              </div>
            ) : (
              discoveredModules.map((m) => {
                const checked = config.modules.length === 0 || config.modules.includes(m);
                return (
                  <label
                    key={m}
                    className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/[0.03] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!isSuperAdmin}
                      onChange={() => {
                        if (!isSuperAdmin) return;
                        // 进入显式选择模式：以当前实际生效集合为起点
                        const base =
                          config.modules.length === 0 ? discoveredModules.slice() : config.modules.slice();
                        const next = base.includes(m) ? base.filter((x) => x !== m) : [...base, m];
                        onUpdate({ modules: next });
                      }}
                      className="accent-violet-500"
                    />
                    <span className="text-[10px] font-mono text-gray-300">{moduleLabel(m)}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* 上次自动备份时间 */}
        <LastAutoInfo />
      </div>
    </div>
  );
}

/** 显示上次自动备份时间 */
function LastAutoInfo() {
  const [last, setLast] = useState<string | null>(null);
  useEffect(() => {
    setLast(localStorage.getItem(LAST_AUTO_KEY));
  }, []);
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
      <div className="text-[10px] font-mono text-gray-500">上次自动备份</div>
      <div className="text-[11px] font-mono text-gray-300">
        {last ? formatTime(last) : "从未执行"}
      </div>
    </div>
  );
}

/** 开关行 */
function ToggleRow({
  icon,
  label,
  desc,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-2">
      <div className="flex items-start gap-2 min-w-0">
        <div className="mt-0.5">{icon}</div>
        <div className="min-w-0">
          <div className="text-[11px] font-medium text-gray-200">{label}</div>
          <div className="text-[9px] font-mono text-gray-600 leading-tight mt-0.5">{desc}</div>
        </div>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
          checked ? "bg-violet-500/60" : "bg-white/[0.08]"
        }`}
      >
        <motion.span
          layout
          className="absolute top-0.5 w-4 h-4 rounded-full bg-white"
          style={{ left: checked ? 18 : 2 }}
        />
      </button>
    </div>
  );
}

// ============================================
// 灾难恢复预案组件
// ============================================

function DisasterRecoveryPlan() {
  const steps = [
    {
      title: "确认数据丢失范围",
      detail: "检查关键模块（登录状态、客户数据、知识库等）是否异常或为空，判断是否需要从备份恢复。",
    },
    {
      title: "进入备份恢复面板",
      detail: "登录超级管理员账户，打开 设置中心 → 数据备份与恢复 面板。",
    },
    {
      title: "选择目标备份",
      detail: "在备份列表中选择最近一次状态为“已完成”的备份，展开查看包含模块与校验和。",
    },
    {
      title: "验证备份完整性",
      detail: "点击“验证”按钮，确认校验和匹配，确保备份数据未损坏。",
    },
    {
      title: "执行恢复",
      detail: "点击“恢复”按钮并确认，系统将把快照写回对应的 localStorage key（覆盖当前数据）。",
    },
    {
      title: "刷新页面生效",
      detail: "恢复完成后按 Ctrl+R 刷新浏览器，使所有模块重新加载已恢复的数据。",
    },
    {
      title: "核对恢复结果",
      detail: "逐一检查关键模块数据是否恢复正常，确认登录状态、业务数据完整。",
    },
    {
      title: "无可用备份时的兜底",
      detail: "若本地无备份，联系系统管理员从 Obsidian Vault（XEAOS/Audit/）或服务端备份手动恢复。",
    },
  ];

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      {/* 头部 */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
        <AlertTriangle size={14} className="text-amber-400" />
        <span className="text-[13px] font-medium text-white">灾难恢复预案</span>
        <span className="text-[10px] font-mono text-gray-600">Disaster Recovery Plan</span>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {steps.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            className="flex gap-2.5 rounded-lg border border-white/[0.04] bg-white/[0.01] p-2.5"
          >
            <div className="w-6 h-6 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-mono font-semibold text-amber-400">{i + 1}</span>
            </div>
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-gray-200">{s.title}</div>
              <div className="text-[10px] font-mono text-gray-500 leading-relaxed mt-0.5">{s.detail}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* 提示 */}
      <div className="px-4 pb-3">
        <div className="flex items-start gap-2 rounded-md border border-sky-500/20 bg-sky-500/[0.05] px-3 py-2">
          <FileText size={12} className="text-sky-400 mt-0.5 flex-shrink-0" />
          <div className="text-[10px] font-mono text-sky-300/90 leading-relaxed">
            <strong>预防建议：</strong>开启自动定时备份并设置合理的保留数量（建议 ≥ 10 条）；
            重要操作前手动创建完整备份并备注说明；定期点击"验证"确保备份完整性；
            备份内容仅存储在本地浏览器，更换设备前请确保已恢复或迁移数据。
          </div>
        </div>
      </div>
    </div>
  );
}
